import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { SvgIconHandler } from "../../../atoms";
import { useDeviceType } from "../../../customHooks/useDeviceType";
import ArrowDashIcon from "../../../atoms/Arrow-DashIcon";

import "./table-view.m.css";

export const TableView = ({
  snapshotByRank,
  visibleItemsForList,
  columns,
  setColumns,
  onItemClick,
  onColumnRemove,
}) => {
  const displayItems = (a) => a.show;

  const isMobile = useDeviceType() !== "desktop";
  const scrollContainerRef = useRef(null);
  const tableRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isTableInViewport, setIsTableInViewport] = useState(false);
  const [showArrows, setShowArrows] = useState(false);

  //Exclding some companies from linking
  const normalize = (str) =>
    String(str)
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "") // remove symbols
      .replace(/\s+/g, " ") // collapse spaces
      .trim();

  const excludedCompanies = [
    "Flipkart India",
    "Ingram Micro India",
    "Amazon Seller Services",
    "LG Electronics India",
    "JioStar India",
    "Flipkart Internet",
    "Amazon Web Services India",
    "Instakart Services",
    "Vishal Mega Mart",
    "NMDC Steel",
    "Brainbees Solutions",
    "Aditya Birla Lifestyle Brands",
    "P N Gadgil Jewellers",
    "PhonePe",
    "Jain Resource Recycling",
    "Lenskart Solutions",
    "Myntra Jabong India",
    "Premier Energies",
    "Myntra Designs",
    "Sagility",
    "Amazon Transportation Services",
    "Brahmos Aerospace",
    "National Bank for Financing Infrast. & Development",
    "Tenneco Clean Air India",
    "Credila Financial Services",
  ].map(normalize);

  const isExcluded = (name) => {
    const clean = normalize(name);
    return excludedCompanies.some((ex) => clean.includes(ex));
  };

  const scrollTable = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 350;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 0);
    if (container.scrollWidth) {
      setCanScrollRight(!(container.scrollLeft + container.clientWidth + 1 > container.scrollWidth));
    }
  };

  const checkTableVisibility = () => {
    const table = tableRef.current;
    if (!table) return;

    const rect = table.getBoundingClientRect();
    const isVisible =
      rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    setIsTableInViewport(isVisible);

    // Check if 50% of the table is visible
    const tableHeight = rect.height;
    const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    const is50PercentVisible = visibleHeight >= tableHeight * 0.5;

    // Show arrows when table is visible and 50% or more is in viewport
    setShowArrows((prevShowArrows) => {
      if (isVisible && is50PercentVisible) {
        return true;
      } else if (isVisible && prevShowArrows && rect.top < window.innerHeight * 0.5) {
        // Keep arrows visible if table is still in upper half of viewport and we previously showed them
        return true;
      } else if (isVisible && rect.top < window.innerHeight * 0.3) {
        // Show arrows if table is in upper 30% of viewport (more generous for reappearing)
        return true;
      } else {
        return false;
      }
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    updateScrollState();
    checkTableVisibility();

    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", () => {
      updateScrollState();
      checkTableVisibility();
    });
    window.addEventListener("scroll", () => {
      checkTableVisibility();
      if (showArrows) {
        setCanScrollLeft((prev) => prev);
      }
    });

    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      window.removeEventListener("scroll", checkTableVisibility);
    };
  }, [isTableInViewport, showArrows]);

  // Update visibility when visibleItemsForList changes (load more)
  useEffect(() => {
    setTimeout(() => {
      updateScrollState();
      checkTableVisibility();
    }, 200);

    setTimeout(() => {
      updateScrollState();
      checkTableVisibility();
    }, 500);
  }, [visibleItemsForList]);

  const toggleColumnVisibility = (columnName, columnDisplayName) => {
    if (onColumnRemove) {
      onColumnRemove(columnDisplayName);
    }
    setColumns((prevColumns) =>
      prevColumns.map((column) => (column.value === columnName ? { ...column, show: !column.show } : column))
    );
  };

  // Calculate arrow position based on 50% of viewport height
  const getArrowStyle = (isLeft) => {
    if (!showArrows) return { display: "none" };

    // Calculate 50% of the viewport height
    const viewportMiddle = window.innerHeight / 2;

    // Position arrow at 50% of viewport height, with a small offset for the arrow icon
    const arrowTop = viewportMiddle - 13; // 13px is half of the arrow height (26px)

    return {
      position: "fixed",
      top: `${arrowTop}px`,
      [isLeft ? "left" : "right"]: "20px",
      zIndex: 100,
    };
  };

  return (
    <div styleName="tableWrapper">
      <div styleName="tableContainer" ref={scrollContainerRef}>
        <table styleName="table" ref={tableRef}>
          <thead>
            <tr>
              {columns.filter(displayItems).map((column, index) =>
                !column.isBold ? (
                  <td
                    key={index}
                    onClick={() => toggleColumnVisibility(column.value, column.name)}
                    styleName="cell remove"
                  >
                    <span>Remove</span>
                    <SvgIconHandler
                      iconStyle={{ color: "#DE5252" }}
                      type="close"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                    />
                  </td>
                ) : (
                  <td key={index} styleName="empty-cell"></td>
                )
              )}
            </tr>
            <tr>
              {columns.filter(displayItems).map((column, index) => (
                <th key={index} styleName="table-header">
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {snapshotByRank.slice(0, visibleItemsForList).map((entityData, index) => (
              <tr key={index}>
                {columns.filter(displayItems).map((c, key) => (
                  <td
                    key={`${index - key}`}
                    styleName={`cell ${key === 1 ? "longname" : ""} ${key <= 1 ? "bold" : ""}`}
                  >
                    {key === 1 ? (
                      isExcluded(entityData[c.value]) ? (
                        <span style={{ fontWeight: "bold" }}>{entityData[c.value]}</span>
                      ) : (
                        <a
                          style={{ fontWeight: "bold" }}
                          href={`/companies/${
                            entityData["fortune500-snapshot-company"]?.slug ||
                            entityData["next500-snapshot-company"]?.slug ||
                            entityData["emergingcompanies-snapshot-company"]?.slug
                          }`}
                          onMouseDown={() => onItemClick && onItemClick(entityData[c.value], index + 1)}
                        >
                          {entityData[c.value]}
                        </a>
                      )
                    ) : (
                      entityData[c.value] || "-"
                    )}
                    {c.value === "current-rank" && (
                      <span
                        className={`rank-change ${
                          entityData["rank-change"] && entityData["rank-change"] < 0 ? "negative-rank" : ""
                        }`}
                      >
                        {entityData["rank-change"] && entityData["rank-change"] > 0
                          ? `+${entityData["rank-change"]}`
                          : entityData["rank-change"]}
                      </span>
                    )}
                    {c.value === "name" && <span className={`rank-change neutral`}>{entityData.ownership}</span>}
                    {c.subvalue && entityData[c.subvalue] !== "" && (
                      <span
                        className={`rank-change ${
                          parseFloat(entityData[c.subvalue]?.toString().replace("%", "")) < 0 ? "negative-rank" : ""
                        }
                        `}
                      >
                        {entityData[c.subvalue]
                          ? entityData[c.subvalue] > 0
                            ? `+${entityData[c.subvalue]}`
                            : `${entityData[c.subvalue]}`
                          : ""}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Scroll Arrows - positioned at 50% of table height */}
      {!isMobile && (
        <>
          {canScrollLeft && (
            <button
              styleName="scroll-arrow left-arrow"
              onClick={() => scrollTable("left")}
              aria-label="Scroll left"
              style={getArrowStyle(true)}
            >
              <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
            </button>
          )}
          {canScrollRight && (
            <button
              styleName="scroll-arrow right-arrow"
              onClick={() => scrollTable("right")}
              aria-label="Scroll right"
              style={getArrowStyle(false)}
            >
              <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

TableView.propTypes = {
  snapshotByRank: PropTypes.arrayOf(PropTypes.object).isRequired,
  visibleItemsForList: PropTypes.number.isRequired,
  columns: PropTypes.array,
  setColumns: PropTypes.func,
  onItemClick: PropTypes.func,
  onColumnRemove: PropTypes.func,
};
